import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import GstTypeModal from './GstTypelModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface GstType {
  gst_type_id: number;
  gst_type_name: string;
  gst_type_status: number;
}

const GstTypes: React.FC = () => {
  const [gstTypes, setGstTypes] = useState<GstType[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<GstType | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchGstTypes = async () => {
    try {
      const data = await apiRequest<GstType[]>({
        method: 'get',
        url: 'gst-type',
      });
      setGstTypes(data);
    } catch (error) {
      showError('Failed to fetch GST types');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGstTypes();
  }, []);

  const handleEditClick = (record: GstType) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchGstTypes();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (gst_type_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-gst-type/${gst_type_id}`,
      });
      showSuccess('GST type deleted');
      fetchGstTypes();
    } catch {
      showError('Failed to delete GST type');
    }
  };

  const handleStatusToggle = async (gstTypeId: number) => {
    setGstTypes((prev) =>
      prev.map((item) =>
        item.gst_type_id === gstTypeId
          ? { ...item, gst_type_status: item.gst_type_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-gst-type/${gstTypeId}`,
      });
      showSuccess('GST type status updated');
    } catch (error) {
      showError('Failed to update status');
      setGstTypes((prev) =>
        prev.map((item) =>
          item.gst_type_id === gstTypeId
            ? { ...item, gst_type_status: item.gst_type_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<GstType>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<GstType> = [
    {
      title: 'GST Type Name',
      dataIndex: 'gst_type_name',
      key: 'gst_type_name',
      filters: Array.from(new Set(gstTypes.map((g) => g.gst_type_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.gst_type_name || null,
      onFilter: (value, record) => record.gst_type_name.includes(value as string),
      sorter: (a, b) => a.gst_type_name.localeCompare(b.gst_type_name),
      sortOrder: sortedInfo.columnKey === 'gst_type_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.gst_type_name}</div>,
    },
  ];

  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: GstType) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.gst_type_status}
                onChange={() => handleStatusToggle(record.gst_type_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this GST type?"
              onConfirm={() => handleDelete(record.gst_type_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb
          style={{ display: 'inline-block', margin: 0 }}
          items={[
            {
              href: '/',
              title: <HomeOutlined />,
            },
            {
              title: 'View GST Types',
            },
          ]}
        />

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add GST Type
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<GstType>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="gst_type_id"
          dataSource={gstTypes.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No GST type found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, gstTypes.length)} of{' '}
                {gstTypes.length}
              </span>
              <Pagination
                {...pagination}
                total={gstTypes.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <GstTypeModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default GstTypes;
